import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import useApi from '../../../utils/AxiosWrapper';
import { ClipLoader } from 'react-spinners';
import { useUser } from '../../../utils/UserContext';
import SignupFooter from '../SignupFooter';
import { Entity } from '../../../types/apiTypes';
import { useScreenView } from '../../../utils/ScreenViewContext';
import {
  createVendorContactInfo,
  PersonalFormData,
  updateVendorSignupStep,
} from './VendorSignupApis';
import { AddressComponents } from '../../../utils/AddressUtils';
import FlexibleAddressInput from '../customers/FlexibleAddressInput';
import {
  safeSessionGetItem,
  safeSessionSetItem,
} from '../../../shared/safeStorage';

interface VendorInfoProps {
  currStep: string;
  selectedRole: Entity;
  setStep: (step: string) => void;
}

function VendorPersonalInfo({
  currStep,
  selectedRole,
  setStep,
}: VendorInfoProps) {
  const { callProtectedApi } = useApi();
  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
    watch,
  } = useForm<PersonalFormData>();
  const { userRecord, userId } = useUser();
  const [loading, setLoading] = useState(false);
  const { screenView } = useScreenView();
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const [addressSelectedFromDropdown, setAddressSelectedFromDropdown] =
    useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [addressFields, setAddressFields] = useState<AddressComponents>({});
  const termsAccepted = watch('termsAccepted');

  const onSubmit: SubmitHandler<PersonalFormData> = async (data) => {
    safeSessionSetItem('vendorPersonalInfo', JSON.stringify(data));
    const success = await createVendorContactInfo(
      data,
      userId ?? '',
      callProtectedApi,
      handleSetLoading,
      setStep,
      'vendor_management_contact'
    );
    if (success) {
      await updateVendorSignupStep(
        userId ?? '',
        callProtectedApi,
        handleSetLoading,
        setStep,
        'vendor_management_contact'
      );
    }
  };

  const handleSetLoading = (loading: boolean) => {
    setLoading(loading);
  };

  useEffect(() => {
    for (const key in addressFields) {
      if (addressFields.hasOwnProperty(key)) {
        setValue(
          key as keyof PersonalFormData,
          addressFields[key as keyof AddressComponents]
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressFields]);

  useEffect(() => {
    // Retrieve form data from session storage
    const savedData = safeSessionGetItem('vendorPersonalInfo');
    if (savedData) {
      const parsedData: PersonalFormData = JSON.parse(savedData);
      // Set form data using setValue
      for (const key in parsedData) {
        if (parsedData.hasOwnProperty(key)) {
          setValue(
            key as keyof PersonalFormData,
            parsedData[key as keyof PersonalFormData]
          );
        }
      }
      if (parsedData?.address_city?.length) {
        setAddressSelectedFromDropdown(true);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue]);

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <div
            className={`flex h-full w-full flex-col items-center justify-start gap-y-[32px] overflow-auto py-[32px] align-middle`}
          >
            {!loading ? (
              <>
                <div className={`'w-[60%] mb-6 flex flex-row items-start`}>
                  <div className="flex flex-col items-center">
                    <h2 className="text-start text-[30px] font-bold">
                      Personal Info
                    </h2>
                    <p className="text-gray-500">
                      Fill in your personal info to get started.
                    </p>
                  </div>
                </div>
                {userRecord && userId && userId?.length > 0 ? (
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className={`w-[60%] space-y-4`}
                  >
                    <div className="flex w-full flex-row items-center gap-x-[16px] align-middle">
                      <div className="w-[50%]">
                        <label
                          htmlFor="firstName"
                          className="block text-sm font-medium text-gray-700"
                        >
                          First Name
                        </label>
                        <input
                          type="text"
                          placeholder="John"
                          {...register('firstName', {
                            required: 'First name is required',
                          })}
                          id="firstName"
                          className={`mt-1 block w-full border px-3 py-2 ${errors.firstName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                        />
                        {errors.firstName && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.firstName.message}
                          </p>
                        )}
                      </div>

                      <div className="w-[50%]">
                        <label
                          htmlFor="lastName"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Last Name
                        </label>
                        <input
                          type="text"
                          placeholder="Doe"
                          {...register('lastName', {
                            required: 'Last name is required',
                          })}
                          id="lastName"
                          className={`mt-1 block w-full border px-3 py-2 ${errors.lastName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                        />
                        {errors.lastName && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.lastName.message}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex w-full flex-row items-center gap-x-[16px] align-middle">
                      <div className="w-[50%]">
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Email
                        </label>
                        <input
                          type="email"
                          placeholder="johndoe@example.com"
                          {...register('email', {
                            required: 'Email is required',
                            pattern: {
                              value: /^\S+@\S+$/i,
                              message: 'Invalid email address',
                            },
                          })}
                          id="email"
                          className={`mt-1 block w-full border px-3 py-2 ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                        />
                        {errors.email && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.email.message}
                          </p>
                        )}
                      </div>

                      <div className="w-[50%]">
                        <label
                          htmlFor="phoneNumber"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Phone Number
                        </label>
                        <input
                          placeholder="1234567890"
                          type="tel"
                          {...register('phoneNumber', {
                            required: 'Phone number is required',
                            pattern: {
                              value: /^\+1[0-9]{10}$/,
                              message:
                                'Phone number must be in the format +1XXXXXXXXXX',
                            },
                            onChange: (e) => {
                              // Automatically format the input to start with +1
                              if (!e.target.value.startsWith('+1')) {
                                e.target.value = `+1${e.target.value.replace(/^\+1|[^0-9]/g, '')}`;
                              }
                            },
                          })}
                          id="phoneNumber"
                          className={`mt-1 block w-full border px-3 py-2 ${errors.phoneNumber ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                        />
                        {errors.phoneNumber && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.phoneNumber.message}
                          </p>
                        )}
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="companyPosition"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Position in the Company
                      </label>
                      <input
                        type="text"
                        placeholder="Owner"
                        {...register('companyPosition', {
                          required: 'Company Position is Required',
                        })}
                        id="companyPosition"
                        className={`mt-1 block w-full border px-3 py-2 ${errors.companyPosition ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                      />
                      {errors.companyPosition && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.companyPosition.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="address"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Address
                      </label>
                      <FlexibleAddressInput
                        searchFieldName="address"
                        streetNumberFieldName="address_st_number"
                        streetNameFieldName="address_st_name"
                        unitFieldName="address_unit"
                        cityFieldName="address_city"
                        stateFieldName="address_state"
                        zipFieldName="address_zip"
                        register={register}
                        control={control}
                        watch={watch}
                        setValue={setValue}
                        errors={errors}
                        initialMode="autocomplete"
                        searchPlaceholder="123 Main St, City, State"
                        onAddressComponentsChange={setAddressFields}
                        addressSelectedFromDropdown={
                          addressSelectedFromDropdown
                        }
                        setAddressSelectedFromDropdown={
                          setAddressSelectedFromDropdown
                        }
                        cookieKey="addressVendorPersonalInfo"
                      />
                    </div>
                    <PrimaryOwnerCheckboxVendor
                      register={register}
                      errors={errors}
                    />
                    <SignupFooter
                      selectedRole={selectedRole}
                      currStep={currStep}
                      onBackClick={() => setStep('vendor_business_info')}
                      disableSubmit={!termsAccepted}
                    />
                  </form>
                ) : (
                  <ClipLoader
                    color="#1D213B"
                    loading={pageLoading}
                    size={60}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                    className="h-full w-full justify-center"
                  />
                )}
              </>
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={loading}
                size={60}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="h-full w-full items-center justify-center"
              />
            )}
          </div>
        );
      case 'tablet':
        return (
          <div
            className={`flex h-full w-full flex-col items-center justify-start gap-y-[32px] overflow-auto py-[32px] align-middle`}
          >
            {!loading ? (
              <>
                <div className={`'w-[60%] mb-6 flex flex-row items-start`}>
                  <div className="flex flex-col items-center">
                    <h2 className="text-start text-[30px] font-bold">
                      Personal Info
                    </h2>
                    <p className="text-gray-500">
                      Fill in your personal info to get started.
                    </p>
                  </div>
                </div>
                {userRecord && userId && userId?.length > 0 ? (
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className={`w-[60%] space-y-4`}
                  >
                    <div className="flex w-full flex-row items-center gap-x-[16px] align-middle">
                      <div className="w-[50%]">
                        <label
                          htmlFor="firstName"
                          className="block text-sm font-medium text-gray-700"
                        >
                          First Name
                        </label>
                        <input
                          type="text"
                          placeholder="John"
                          {...register('firstName', {
                            required: 'First name is required',
                          })}
                          id="firstName"
                          className={`mt-1 block w-full border px-3 py-2 ${errors.firstName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                        />
                        {errors.firstName && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.firstName.message}
                          </p>
                        )}
                      </div>

                      <div className="w-[50%]">
                        <label
                          htmlFor="lastName"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Last Name
                        </label>
                        <input
                          type="text"
                          placeholder="Doe"
                          {...register('lastName', {
                            required: 'Last name is required',
                          })}
                          id="lastName"
                          className={`mt-1 block w-full border px-3 py-2 ${errors.lastName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                        />
                        {errors.lastName && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.lastName.message}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex w-full flex-row items-center gap-x-[16px] align-middle">
                      <div className="w-[50%]">
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Email
                        </label>
                        <input
                          type="email"
                          placeholder="johndoe@example.com"
                          {...register('email', {
                            required: 'Email is required',
                            pattern: {
                              value: /^\S+@\S+$/i,
                              message: 'Invalid email address',
                            },
                          })}
                          id="email"
                          className={`mt-1 block w-full border px-3 py-2 ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                        />
                        {errors.email && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.email.message}
                          </p>
                        )}
                      </div>

                      <div className="w-[50%]">
                        <label
                          htmlFor="phoneNumber"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Phone Number
                        </label>
                        <input
                          placeholder="1234567890"
                          type="tel"
                          {...register('phoneNumber', {
                            required: 'Phone number is required',
                            pattern: {
                              value: /^\+1[0-9]{10}$/,
                              message:
                                'Phone number must be in the format +1XXXXXXXXXX',
                            },
                            onChange: (e) => {
                              // Automatically format the input to start with +1
                              if (!e.target.value.startsWith('+1')) {
                                e.target.value = `+1${e.target.value.replace(/^\+1|[^0-9]/g, '')}`;
                              }
                            },
                          })}
                          id="phoneNumber"
                          className={`mt-1 block w-full border px-3 py-2 ${errors.phoneNumber ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                        />
                        {errors.phoneNumber && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.phoneNumber.message}
                          </p>
                        )}
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="companyPosition"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Position in the Company
                      </label>
                      <input
                        type="text"
                        placeholder="Owner"
                        {...register('companyPosition', {
                          required: 'Company Position is Required',
                        })}
                        id="companyPosition"
                        className={`mt-1 block w-full border px-3 py-2 ${errors.companyPosition ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                      />
                      {errors.companyPosition && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.companyPosition.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="address"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Address
                      </label>
                      <FlexibleAddressInput
                        searchFieldName="address"
                        streetNumberFieldName="address_st_number"
                        streetNameFieldName="address_st_name"
                        unitFieldName="address_unit"
                        cityFieldName="address_city"
                        stateFieldName="address_state"
                        zipFieldName="address_zip"
                        register={register}
                        control={control}
                        watch={watch}
                        setValue={setValue}
                        errors={errors}
                        initialMode="autocomplete"
                        searchPlaceholder="123 Main St, City, State"
                        onAddressComponentsChange={setAddressFields}
                        addressSelectedFromDropdown={
                          addressSelectedFromDropdown
                        }
                        setAddressSelectedFromDropdown={
                          setAddressSelectedFromDropdown
                        }
                        cookieKey="addressVendorPersonalInfo"
                      />
                    </div>
                    <PrimaryOwnerCheckboxVendor
                      register={register}
                      errors={errors}
                    />
                    <SignupFooter
                      selectedRole={selectedRole}
                      currStep={currStep}
                      onBackClick={() => setStep('vendor_business_info')}
                      disableSubmit={!termsAccepted}
                    />
                  </form>
                ) : (
                  <ClipLoader
                    color="#1D213B"
                    loading={pageLoading}
                    size={60}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                    className="h-full w-full justify-center"
                  />
                )}
              </>
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={loading}
                size={60}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="h-full w-full items-center justify-center"
              />
            )}
          </div>
        );
      case 'mobile':
        return (
          <div
            className={`flex h-full w-full flex-col items-center justify-start gap-y-[32px] overflow-auto py-[32px] align-middle`}
          >
            {!loading ? (
              <>
                <div className={`'w-full mb-6 flex flex-row items-start`}>
                  <div className="flex flex-col items-center">
                    <h2 className="text-start text-[30px] font-bold">
                      Personal Info
                    </h2>
                    <p className="text-gray-500">
                      Fill in your personal info to get started.
                    </p>
                  </div>
                </div>
                {userRecord && userId && userId?.length > 0 ? (
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className={`w-full space-y-4`}
                  >
                    <div>
                      <label
                        htmlFor="firstName"
                        className="block text-sm font-medium text-gray-700"
                      >
                        First Name
                      </label>
                      <input
                        type="text"
                        placeholder="John"
                        {...register('firstName', {
                          required: 'First name is required',
                        })}
                        id="firstName"
                        className={`mt-1 block w-full border px-3 py-2 ${errors.firstName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                      />
                      {errors.firstName && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.firstName.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="lastName"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Last Name
                      </label>
                      <input
                        type="text"
                        placeholder="Doe"
                        {...register('lastName', {
                          required: 'Last name is required',
                        })}
                        id="lastName"
                        className={`mt-1 block w-full border px-3 py-2 ${errors.lastName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                      />
                      {errors.lastName && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.lastName.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Email
                      </label>
                      <input
                        type="email"
                        placeholder="johndoe@example.com"
                        {...register('email', {
                          required: 'Email is required',
                          pattern: {
                            value: /^\S+@\S+$/i,
                            message: 'Invalid email address',
                          },
                        })}
                        id="email"
                        className={`mt-1 block w-full border px-3 py-2 ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.email.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="phoneNumber"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Phone Number
                      </label>
                      <input
                        placeholder="1234567890"
                        type="tel"
                        {...register('phoneNumber', {
                          required: 'Phone number is required',
                          pattern: {
                            value: /^\+1[0-9]{10}$/,
                            message:
                              'Phone number must be in the format +1XXXXXXXXXX',
                          },
                          onChange: (e) => {
                            // Automatically format the input to start with +1
                            if (!e.target.value.startsWith('+1')) {
                              e.target.value = `+1${e.target.value.replace(/^\+1|[^0-9]/g, '')}`;
                            }
                          },
                        })}
                        id="phoneNumber"
                        className={`mt-1 block w-full border px-3 py-2 ${errors.phoneNumber ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                      />
                      {errors.phoneNumber && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.phoneNumber.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="companyPosition"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Position in the Company
                      </label>
                      <input
                        type="text"
                        placeholder="Owner"
                        {...register('companyPosition', {
                          required: 'Company Position is Required',
                        })}
                        id="companyPosition"
                        className={`mt-1 block w-full border px-3 py-2 ${errors.companyPosition ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                      />
                      {errors.companyPosition && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.companyPosition.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="address"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Address
                      </label>
                      <FlexibleAddressInput
                        searchFieldName="address"
                        streetNumberFieldName="address_st_number"
                        streetNameFieldName="address_st_name"
                        unitFieldName="address_unit"
                        cityFieldName="address_city"
                        stateFieldName="address_state"
                        zipFieldName="address_zip"
                        register={register}
                        control={control}
                        watch={watch}
                        setValue={setValue}
                        errors={errors}
                        initialMode="autocomplete"
                        searchPlaceholder="123 Main St, City, State"
                        onAddressComponentsChange={setAddressFields}
                        addressSelectedFromDropdown={
                          addressSelectedFromDropdown
                        }
                        setAddressSelectedFromDropdown={
                          setAddressSelectedFromDropdown
                        }
                        cookieKey="addressVendorPersonalInfo"
                      />
                      {errors.address && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.address.message}
                        </p>
                      )}
                    </div>
                    <PrimaryOwnerCheckboxVendor
                      register={register}
                      errors={errors}
                    />
                    <SignupFooter
                      selectedRole={selectedRole}
                      currStep={currStep}
                      onBackClick={() => setStep('vendor_business_info')}
                      isMobile={true}
                      disableSubmit={!termsAccepted}
                    />
                  </form>
                ) : (
                  <ClipLoader
                    color="#1D213B"
                    loading={pageLoading}
                    size={60}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                    className="h-full w-full justify-center"
                  />
                )}
              </>
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={loading}
                size={60}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="h-full w-full items-center justify-center"
              />
            )}
          </div>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default VendorPersonalInfo;

const PrimaryOwnerCheckboxVendor = ({
  register,
  errors,
}: {
  register: any;
  errors: any;
}) => (
  <div className="pt-4">
    <div className="flex flex-row items-center">
      <div className="flex h-5 items-center">
        <input
          id="termsAccepted"
          type="checkbox"
          {...register('termsAccepted', {
            required: 'You must accept the terms and conditions to continue',
          })}
          className="h-8 w-8 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      </div>
      <div className="ml-3 text-sm">
        <label
          htmlFor="termsAccepted"
          className="text-[16px] font-medium text-gray-700"
        >
          I confirm that I am the primary owner of this business.
        </label>
      </div>
    </div>
    {errors.termsAccepted && (
      <p className="mt-1 text-sm text-red-500">
        {errors.termsAccepted.message}
      </p>
    )}
  </div>
);
